<template>
  <div>
    <b>Samples</b>
    <v-row>
      <v-col>
        <samples-list ref="samples-list"
                      v-if="internalSamples"
                      :selectedClientId="selectedClientId"
                      :items="internalSamplesPrepared"
                      :suitableBatch="suitableBatch"
                      @validation-changed="handleSamplesValidationChanged"
                      @delete="handleDeleteSample"
                      @change-fertilization-method-for-all="handleChangeFertilizationMethodForAll"
                      @sample-target-changed="handleSampleTargetChanged"
                      @close-sample-edit-menu="handleCloseSampleEditMenu"></samples-list>
      </v-col>
    </v-row>
    <v-btn color="success" @click="samplesAddButtonClick">Add</v-btn>
    <sample-add-dialog ref="samples-manager" @add="handleAddSample"></sample-add-dialog>
  </div>
</template>

<script>
import SamplesList from './List'
import SampleAddDialog from './SampleAddDialog'
import {mapActions, mapGetters, mapState} from 'vuex'
import * as SampleTargets from '../../dictionaries/sample-targets'
import * as SampleDaysTypes from '../../dictionaries/sample-days-types'
import _ from 'lodash'
import moment from 'moment'
import {naturalCompare, isNumeric} from 'js-core/helpers/numbers'

export default {
  name: "SamplesManager",
  components: {
    SamplesList,
    SampleAddDialog
  },
  props: {
    samples: Array,
    selectedClientId: String,
    selectedEmbryologists: Array
  },
  data() {
    let internalSamples = _.get(this, 'samples', [])
    let internalSamplesWithTempId = this.addTempIdToInternalSamples(internalSamples)

    return {
      internalSamples: internalSamplesWithTempId,
      samplesAddMenu: false,
    }
  },
  watch: {
    samples() {
      let internalSamples = _.get(this, 'samples', [])
      this.internalSamples = this.addTempIdToInternalSamples(internalSamples)
    }
  },
  computed: {
    ...mapGetters('pgtCases', {
      availableFertilizationMethodsByValue: 'availableFertilizationMethodsByValue',
      progress: 'progress'
    }),
    ...mapState('pgtCases', {
      suitableBatch: state => _.get(state, 'suitableBatch')
    }),
    ...mapState('clients', {
      availableClients: 'clients'
    }),
    ...mapGetters('clients', {
      clientsById: 'clientsById'
    }),
    internalSamplesPrepared() {
      return _.orderBy(this.internalSamples, ['orderNumber'], ['asc'])
    }
  },
  mounted() {
    this.calculateSuitableBatch()
  },
  methods: {
    ...mapActions('pgtCases', {
      getSuitableBatch: 'getSuitableBatch'
    }),
    samplesAddButtonClick() {
      this.$refs['samples-manager'].show()
    },
    handleAddSample(item) {
      this.createSamples(item.prefix, item.count)
    },
    calculateSuitableBatch() {
      const samplesCountForAnalysis = _.filter(this.internalSamples, {
        target: SampleTargets.Analysis,
        batchId: null
      }).length

      if (samplesCountForAnalysis <= 0) {
        return
      }

      this.getSuitableBatch(samplesCountForAnalysis)
    },
    createSamples(prefix, count) {
      const currentClient = this.clientsById[this.selectedClientId] || null
      const defaultPeriodExecution = _.get(currentClient, ['periodExecution'], null)

      let lastSample = _.maxBy(this.internalSamples, 'orderNumber')
      let lastSampleTitle = _.get(lastSample, 'title', '')

      let orderNumber = _.get(lastSample, 'orderNumber', 0);

      let lastSampleTitleOrder = orderNumber

      if (lastSampleTitle.length > 0) {
        let cumulativeLastOrderNumber = ''
        for (let i = lastSampleTitle.length -1; i >= 0; i--) {
          if (!isNumeric((lastSampleTitle[i]))) {
            break;
          }
          cumulativeLastOrderNumber = `${lastSampleTitle[i]}${cumulativeLastOrderNumber}`
        }
        lastSampleTitleOrder = parseInt(cumulativeLastOrderNumber)
      }

      orderNumber++;

      let result = [];
      const endOrderNumber = orderNumber + count
      for (; orderNumber < endOrderNumber; orderNumber++) {
        result.push({
          tempId: _.uniqueId(),
          orderNumber: orderNumber,
          title: `${prefix}${++lastSampleTitleOrder}`,
          fertilizationMethod: null,
          target: SampleTargets.Analysis,
          batchId: null,
          applicationDatetime: moment().set({
            hour: 12,
            minute: 0,
            second: 0,
            millisecond: 0
          }).toISOString(),
          daysType: SampleDaysTypes.Work,
          periodExecution: defaultPeriodExecution,
          estimatedCompletionDatetime: null,
          priority: false,
          targetChanged: true,
          biopsyEmbryologist: null
        })
      }

      this.internalSamples = _.concat(this.internalSamples, result)

      this.$nextTick(() => {
        this.validate()
      })

      this.calculateSuitableBatch()
    },
    handleDeleteSample(id) {
      const sampleId = _.findIndex(this.internalSamples, function (s) {
        return s.tempId === id || s.id === id
      })

      if (sampleId < 0) {
        return
      }

      this.internalSamples.splice(sampleId, 1)
    },
    handleChangeFertilizationMethodForAll(fertilizationMethod) {
      for (let sample of this.internalSamples) {
        sample.fertilizationMethod = fertilizationMethod
      }
    },
    handleSamplesValidationChanged(value) {
      this.$emit('validation-changed', value)
    },
    handleSampleTargetChanged() {
      this.calculateSuitableBatch()
    },
    isValid() {
      return this.$refs['samples-list'] && this.$refs['samples-list'].isValid() && this.internalSamples.length > 0
    },
    validate() {
      return this.$refs['samples-list'] && this.$refs['samples-list'].validate() && this.internalSamples.length > 0
    },
    getSamples() {
      return _.map(this.internalSamples, (s) => {
        return {
          id: _.get(s, 'id', null),
          title: s.title,
          orderNumber: s.orderNumber,
          fertilizationMethod: s.fertilizationMethod,
          target: _.get(s, 'target', 'archive'),
          applicationDatetime: _.get(s, 'applicationDatetime', null),
          daysType: _.get(s, 'daysType', null),
          periodExecution: _.get(s, 'periodExecution', null),
          priority: _.get(s, 'priority', false),
          targetChanged: _.get(s, 'targetChanged', false),
          day: _.get(s, 'day', null),
          blastocystQuality: _.get(s, 'blastocystQuality', null),
          clientComment: _.get(s, 'clientComment', null),
          biopsyEmbryologist: _.get(s, 'biopsyEmbryologist', null)
        }
      })
    },
    handleCloseSampleEditMenu() {
      this.reorderInternalSamples()
    },
    reorderInternalSamples() {
      const sortedInternalSamples = _.cloneDeep(this.internalSamples).sort(function (a, b) {
        return naturalCompare(a.title, b.title)
      })

      let changed = false
      for (let i = 0; i < sortedInternalSamples.length; i++) {
        if (sortedInternalSamples[i].tempId !== this.internalSamples[i].tempId) {
          changed = true
        }

        sortedInternalSamples[i].orderNumber = i
      }

      if (changed) {
        this.internalSamples = sortedInternalSamples

        this.$nextTick(() => {
          this.validate()
        })
      }

      return changed
    },
    addTempIdToInternalSamples(internalSamples = []) {
      for (let i = 0; i < internalSamples.length; i++) {
        internalSamples[i].tempId = _.uniqueId()
      }

      return internalSamples
    }
  }
}
</script>

<style scoped>

</style>